/**
 * English locale strings.
 *
 * Keys are namespaced by domain. Interpolation uses i18next syntax:
 * {{variable}}. Every key used in src/ must appear here so that the
 * fallback default in `t('key', 'default')` never leaks through (it
 * would do so in BOTH languages otherwise, breaking the Spanish
 * experience).
 *
 * es.ts mirrors this shape exactly — the i18n locale-completeness
 * test (lib/i18n/i18n.test.ts) refuses any drift.
 */

const en = {
  auth: {
    signInTo: 'Sign in to {{name}}',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    signingIn: 'Signing in…',
    signIn: 'Sign in',
    signOut: 'Sign out',
    accessDenied: 'Access denied',
    accessDeniedDetail:
      'Your account does not have permission to view this page. Contact your supervisor if you believe this is an error.',
    goToDashboard: 'Go to dashboard',
    biometricSignIn: 'Sign in with biometric',
    biometricVerifying: 'Verifying…',
    orWithPin: 'or sign in with PIN',
    pinLabel: 'PIN',
    pinHint: '6-digit PIN from the welcome email.',
    forgotPinOpen: 'Forgot PIN?',
    forgotPinClose: 'Cancel',
    forgotPinSending: 'Sending…',
    forgotPinSubmit: 'Send me a new PIN',
    forgotPinSent:
      'If that email is registered, a new PIN has been sent. Check your inbox.',
  },

  biometric: {
    promptLabel: 'Biometric sign-in',
    promptTitle: 'Speed up next sign-in',
    promptBody: 'Enable Face ID / fingerprint to sign in without typing your PIN.',
    promptEnable: 'Enable',
    promptEnrolling: 'Setting up…',
    promptNotNow: 'Not now',
  },

  dashboard: {
    myTasks: 'My tasks',
    tapToStart: 'Tap a task to see its checklist and start cleaning.',
    loadingTasks: 'Loading your tasks…',
    failedToLoadTasks: 'Failed to load tasks',
    noTasksAssigned: 'You have no tasks assigned right now.',
    room: 'Room {{number}}',
    clean: '{{type}} clean',
    started: 'started {{date}}',
    availableJobs: 'Available jobs',
    tapToTake: 'No job assigned? Tap an available job to take it.',
    noAvailableJobs: 'No jobs are available right now. Check back in a few minutes.',
    takeBadge: 'Tap to take',
  },

  task: {
    loadingTask: 'Loading task…',
    failedToLoadTask: 'Failed to load task',
    taskNotFound: 'Task not found.',
    startedLabel: 'Started:',
    notYet: 'not yet',
    completedLabel: 'Completed:',
    blockingIssues: 'Blocking issues',
    blockingDetail:
      'this room cannot be marked ready until these are resolved:',
    tip: 'Tip',
    loadingAI: 'Loading AI guidance…',
    coachingUnavailable:
      'AI guidance is temporarily unavailable. The grounded checklist above is current — work from it and your training.',
    required: 'Required ({{done}}/{{total}})',
    optional: 'Optional',
    noRequiredItems: 'No required items for this room type.',
    photos: 'Photos',
    markComplete: 'Mark done',
    markIncomplete: 'Mark not done',
    markNa: 'N/A',
    clearNa: 'Clear N/A',
    naReasonLabel: 'Reason (optional, ≤200 chars)',
    naSave: 'Mark N/A',
    naCancel: 'Cancel',
    partialScopeLabel: 'Partial scope:',
  },

  photoCategory: {
    bathroom_mirror:   'Bathroom mirror',
    bathroom_sink:     'Bathroom sink',
    bathroom_toilet:   'Toilet',
    bathroom_tub:      'Bathtub / shower',
    bedroom_overview:  'Bedroom overview',
    bedroom_floor:     'Bedroom floor',
    bedroom_closet:    'Closet',
    bedroom_surfaces:  'Bedroom surfaces',
    trash_bags:        'Trash bags',
    appliances:        'Appliances',
    ceiling_fan:       'Ceiling fan blades',
    baseboards:        'Baseboards',
    oven_interior:     'Inside the oven (door open)',
    stove_cooktop:     'Stove cooktop',
    before:            'Before photo',
    after:             'After photo',
    general:           'General',
    damage:            'Damage',
    inventory:         'Inventory',
  },

  startTask: {
    panelLabel:        'Start cleaning',
    prompt:            'Ready to begin? Tapping start marks the task in progress and moves the room into cleaning.',
    unassigned:        'This task has not been assigned to a cleaner yet. Assign someone before starting.',
    unassignedCleaner: 'No cleaner is assigned yet. Tap Start to claim this job and begin cleaning.',
    start:             'Start cleaning',
    starting:          'Starting…',
    blockUnassigned:   'A supervisor must assign a cleaner before this task can start.',
    blockNotMine:      'Only the assigned cleaner can start this task.',
    blockRole:         'Your role cannot start this task.',
  },

  assignTask: {
    panelLabel:   'Assign cleaner to task',
    button:       'Assign cleaner',
    cleanerLabel: 'Assign to',
    submitting:   'Assigning…',
    confirm:      'Confirm assignment',
  },

  counters: {
    legend: 'Recorded at submit',
    trashBags: 'Trash bags',
    appliances: 'Appliances cleaned',
  },

  photo: {
    required: 'required',
    uploaded: '✓ {{count}} uploaded',
    preparing: 'Preparing…',
    uploading: 'Uploading…',
    addAnother: 'Add another',
    addPhoto: 'Add photo',
    couldNotProcess: 'Could not process this photo. {{message}}',
    runningVisionCheck: 'Running vision check…',
    verdictOk: 'Looks good',
    verdictRetake: 'Retake recommended',
    verdictUnclear: 'Vision check unsure',
    verdictUnavailable: 'ReadyScan verification temporarily unavailable',
    verdictSkipped: 'Vision check skipped',
  },

  action: {
    submitted: 'Submitted.',
    roomMovedTo: 'Room moved to {{status}}.',
    cannotSubmitYet: 'Cannot submit yet',
    submissionBlocked: 'Submission blocked',
    submitting: 'Submitting…',
    submitForInspection: 'Submit task for inspection',
    checkingPhotos: 'Checking photo coverage…',
    coverageMissing: 'Photo coverage incomplete',
    coverageMissingDetail: 'These required photos were not detected: {{categories}}. Take them and try submitting again.',
    coverageUnavailable: 'ReadyScan verification temporarily unavailable',
    coverageUnavailableBlockerDetail:
      'ReadyScan cannot check your photos right now. If every required photo is uploaded, you can still submit — your supervisor will be alerted to verify manually before the room is marked ready.',
    coverageUnavailableSubmitAnyway: 'Request supervisor approval & submit',
    coverageUnavailablePostSubmitDetail:
      'Submitted with manual-approval flag — your supervisor will verify the photos before the room can be marked ready.',
    coverageUnavailableCancel: 'Wait and retry',
    coverageTruncated: 'ReadyScan checked some of your photos',
    coverageTruncatedDetail:
      'This task has more photos than ReadyScan reviews in one pass, so {{count}} were not checked. The result below covers the rest. If something was only shown in an unchecked photo, retake it so ReadyScan can see it.',
    defectsFound: 'Cleaning issues detected',
    defectsFoundDetail: 'The ReadyScan inspection flagged these issues to re-clean before you can submit: {{defects}}.',
    defectLabels: {
      baseboard_dust: 'Dust on the baseboards',
      tub_ring: 'Ring in the bathtub / shower',
      toilet_ring: 'Ring in the toilet bowl',
      mirror_dirty: 'Dirty or streaky mirror',
      blind_dust: 'Dust on the blinds',
      stove_food: 'Food or grease on the cooktop',
      oven_food: 'Baked-on food inside the oven',
      refrigerator_dirty: 'Refrigerator needs cleaning',
      ceiling_fan_dust: 'Dust on the ceiling fan',
      floor_not_vacuumed: 'Floor not vacuumed',
    },
  },

  abandonedBelongings: {
    heading: 'Abandoned belongings',
    openForm: '+ Report abandoned belongings',
    descriptionLabel: 'Description (1–1000 chars)',
    submit: 'File report',
    cancel: 'Cancel',
  },

  abandonedBelongingsQueue: {
    title: 'Abandoned belongings',
    subtitle: 'Cleaner-filed reports awaiting your decision.',
    loading: 'Loading queue…',
    empty: 'No abandoned-belongings reports waiting for a decision right now.',
    failedToLoad: 'Failed to load the queue.',
    featureDisabled:
      'Abandoned-belongings tracking is not enabled for this organization. Contact your admin to enable the workflow.',
    notesLabel: 'Disposition notes (optional, ≤1000 chars)',
    acknowledge: 'Acknowledge',
    markRemoved: 'Mark removed',
    markBilled: 'Mark billed',
    confirmRemoved: 'Confirm removed',
    confirmBilled: 'Confirm billed',
    cancel: 'Cancel',
  },

  feeRequest: {
    heading: 'Additional fee requests',
    openForm: '+ Request additional fee',
    amountLabel: 'Amount (USD)',
    reasonLabel: 'Reason (1–1000 chars)',
    submit: 'File request',
    cancel: 'Cancel',
  },

  feeRequests: {
    title: 'Additional-fee requests',
    subtitle: 'Cleaner-filed exceedance requests awaiting your decision.',
    loading: 'Loading queue…',
    empty: 'No fee requests waiting for a decision right now.',
    failedToLoad: 'Failed to load the queue.',
    featureDisabled:
      'Additional-fee requests are not enabled for this organization. Contact your admin to enable the workflow.',
    reasonLabel: 'Reason (1–1000 chars)',
    paidAtNormal: 'Paid at normal rate',
    statusRequested: 'Awaiting owner decision',
    statusCompletedWithoutApproval:
      'Task already completed — owner decision still required',
    approve: 'Approve',
    deny: 'Deny',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },

  inspection: {
    heading: 'Inspection history',
    startFirst: 'Start inspection',
    startReinspection: 'Start re-inspection',
    decisionLabel: 'Decision',
    failureReasonLabel: 'Failure reason (1–1000 chars)',
    notesLabel: 'Internal notes (optional, ≤1000 chars)',
    submit: 'Submit decision',
  },

  anomalies: {
    title: 'Anomaly alerts',
    subtitle: 'Cross-cycle scan results — confirm or dismiss to clear.',
    loading: 'Loading alerts…',
    empty: 'No open anomaly alerts. Nice work.',
    failedToLoad: 'Failed to load anomaly alerts.',
    aiUnavailable: 'AI summary unavailable — review the metrics below.',
    detected: 'detected {{when}}',
    addNotes: 'Add notes',
    hideNotes: 'Hide notes',
    notesPlaceholder: 'Optional reviewer notes',
    confirm: 'Confirm',
    dismiss: 'Dismiss',
    working: 'Working…',
    recentReviewed: 'Recently reviewed ({{count}})',
  },

  maintenance: {
    title: 'Maintenance issues',
    subtitle: 'Actionable issues — blocking + critical first.',
    loading: 'Loading issues…',
    empty: 'No actionable issues. Nice work.',
    failedToLoad: 'Failed to load maintenance issues.',
    roomLine: 'Room {{room}} · {{building}} · opened {{when}}',
    severityLabel: 'Severity',
    categoryLabel: 'Category',
    blockingLabel: 'Blocking',
    blocking: 'Blocking',
    notesLabel: 'Resolution notes (required)',
    notesPlaceholder: 'What did you fix?',
    start: 'Start work',
    starting: 'Starting…',
    resolve: 'Resolve',
    resolving: 'Resolving…',
    markResolved: 'Mark resolved',
    aiTriageBanner: 'AI triage suggestion',
    aiSuggests: 'AI suggests',
    aiUnavailable: 'AI triage unavailable — triage manually.',
    aiMalformed: 'AI triage returned malformed output — triage manually.',
    applyAiSuggestion: 'Apply suggestion',
    applying: 'Applying…',
    overrideAiSuggestion: 'Override',
    dismissAiHint: 'Dismiss',
  },

  board: {
    title: 'Room readiness board',
    subtitle: 'Live view of every room, grouped by status (operational urgency).',
    loading: 'Loading rooms…',
    empty: 'No rooms match the current filters.',
    failedToLoad: 'Failed to load the room board.',
    filterCampus: 'Campus',
    filterBuilding: 'Building',
    filterFloor: 'Floor',
    allCampuses: 'All campuses',
    allBuildings: 'All buildings',
    allFloors: 'All floors',
  },

  status: {
    occupied:             'Occupied',
    move_out_pending:     'Move-out pending',
    vacant_dirty:         'Vacant (dirty)',
    cleaning_assigned:    'Cleaning assigned',
    cleaning_in_progress: 'Cleaning in progress',
    inspection_pending:   'Inspection pending',
    maintenance_required: 'Maintenance required',
    ready:                'Ready',
    reserved:             'Reserved',
  },

  taskStatus: {
    pending:     'Pending',
    in_progress: 'In progress',
    completed:   'Awaiting inspection',
    approved:    'Approved',
    rejected:    'Rejected',
  },

  severity: {
    low:      'Low',
    medium:   'Medium',
    high:     'High',
    critical: 'Critical',
  },

  maintenanceIssueStatus: {
    open:        'Open',
    assigned:    'Assigned',
    in_progress: 'In progress',
    resolved:    'Resolved',
    closed:      'Closed',
  },

  roomDetail: {
    loading:            'Loading room…',
    failedToLoad:       'Failed to load the room.',
    notFound:           'Room not found.',
    title:              'Room {{number}}',
    unknownLocation:    'Location unknown',
    unknownType:        'Unknown type',
    bedCount_one:       '{{count}} bed',
    bedCount_other:     '{{count}} beds',
    statusLabel:        'Current status',
    activeTask:         'Active cleaning task',
    noActiveTask:       'No active cleaning task on this room.',
    taskStatusLabel:    'Status',
    taskCycleLabel:     'Cycle',
    taskAssignee:       'Assigned to {{name}}',
    taskUnassigned:     'Unassigned',
    taskStartedAt:      'Started {{when}}',
    taskSubmittedAt:    'Submitted {{when}}',
    openTask:           'Open task →',
    maintenanceIssues:  'Open maintenance issues',
    noIssues:           'No open maintenance issues.',
    blocksReadiness:    'Blocks ready',
    openedAt:           'Opened {{when}}',
    history:            'Recent status changes',
    noHistory:          'No recorded status changes yet.',
    historyTransition:  '{{from}} → {{to}}',
    historyInitial:     'Created as {{to}}',
  },

  supervisor: {
    pendingInspections: 'Pending inspections',
    reviewSubmitted: 'Tasks the cleaners have submitted, oldest first.',
    loading: 'Loading queue…',
    empty: 'No tasks waiting for inspection right now.',
    failedToLoad: 'Failed to load the inspection queue.',
    campusFilterLabel: 'Campus:',
    campusAll: 'All campuses',
    room: 'Room {{number}}',
    cycleType: '{{cycle}} cycle',
    cleanerSubmitted: '{{cleaner}} · submitted {{when}}',
    unknownCleaner: 'Unknown cleaner',
    unknownTime: 'unknown time',
    approve: 'Approve',
    approving: 'Approving…',
    reject: 'Reject',
    rejecting: 'Rejecting…',
    confirmReject: 'Send back',
    rejectReasonLabel: 'Reason (required)',
    rejectReasonPlaceholder: 'What does the cleaner need to fix?',
    newTaskLink: '+ New task',
    governanceLink: 'MCP corrections →',
    aiOverrideChip: 'ReadyScan override — needs your eye',
    myJobs: 'My cleaning jobs',
    myJobsLabel: 'Cleaning jobs assigned to me',
    myJobsHint: 'Jobs assigned to you. Tap to open and start cleaning.',
  },

  createTask: {
    title: 'New cleaning task',
    subtitle:
      'Pick a room and tier. Checklist items are seeded automatically from your contract templates.',
    pickRoom: 'Pick a room',
    room: 'Room',
    roomLabel: 'Room',
    cycle: 'Cycle type',
    tier: 'Service tier',
    assignee: 'Assign to (optional)',
    unassigned: 'Unassigned — assign later',
    estimatedMinutes: 'Estimated minutes (optional)',
    isPartial: 'This is a partial-unit job',
    kitchenExcluded: 'Kitchen excluded',
    partialLegend: 'Partial-unit details',
    partialScope: 'Scope description (≤500 chars, optional)',
    submit: 'Create task',
    creating: 'Creating…',
    loading: 'Loading…',
    cancel: 'Cancel',
  },

  bulkTask: {
    title: 'Bulk create tasks',
    subtitle:
      'Create one task per room matching the scope below. Up to 50 rooms per server batch; larger selections are dispatched in multiple batches automatically.',
    campus: 'Campus (optional filter)',
    anyCampus: 'Any campus',
    building: 'Building',
    anyBuilding: 'Any building',
    floor: 'Floor (optional)',
    anyFloor: 'Any floor',
    pickBuildingFirst: 'Pick a building first',
    cycle: 'Cycle type',
    tier: 'Service tier',
    assignee: 'Assign all to (optional)',
    unassigned: 'Unassigned — assign later per room',
    estimatedMinutes: 'Estimated minutes per task (optional)',
    summaryMatch:
      'Will create {{count}} task(s) across {{batches}} batch(es) of up to {{batchSize}} rooms each.',
    summaryEmpty: 'No rooms match the current scope. Adjust the filters.',
    submit: 'Create {{count}} tasks',
    creating: 'Creating…',
    loading: 'Loading…',
    cancel: 'Cancel',
    resultTitle: 'Result',
    created: 'created',
    failed: 'failed',
  },

  team: {
    title: 'Team',
    subtitle:
      'Everyone in your organization. Invite cleaners and supervisors below — they’ll get a sign-in PIN by email.',
    loading: 'Loading team…',
    loadFailed: 'Failed to load team.',
    empty: 'No team members yet. Invite the first one below.',
    roster: 'Current team',
    roleLabel: 'Role',
    roleCleaner: 'Cleaner',
    roleSupervisor: 'Supervisor',
    promote: 'Make supervisor',
    demote: 'Demote to cleaner',
    updating: 'Saving…',
    inviteTitle: 'Invite team members',
    inviteHelp:
      'Paste one email per line (or separate with commas). Up to 50 at a time. They’ll each get a 6-digit PIN by email and can sign in immediately.',
    emailsLabel: 'Email addresses',
    invite: 'Send invites',
    inviting: 'Sending…',
    invalidEmails: 'These addresses don’t look like valid emails: {{list}}',
    provisionedCount: 'Invited {{count}}',
  },

  reports: {
    title: 'Reports',
    loading: 'Loading reports…',
    error: 'Could not load reports. Try refreshing.',
    roomReadiness: 'Room readiness',
    cleaningOperations: 'Cleaning operations',
    partialUnitApproved: 'Partial-unit completed',
    feeRequests: 'Fee requests',
    exceedanceCount: 'Exceedance events recorded',
    inspectionsByTier: 'Inspections by tier',
    noInspections: 'No inspections recorded yet.',
  },

  leads: {
    title: 'Leads',
    subtitle: 'Inquiries captured from the marketing landing page.',
    loading: 'Loading leads…',
    error: 'Could not load leads.',
    empty: 'No leads in this window.',
    filterVertical: 'Vertical',
    allVerticals: 'All verticals',
    count: '{{count}} shown',
    rooms: '{{count}} rooms',
    properties: '{{count}} properties',
    fitScore: 'Fit {{score}}',
    fitUngraded: 'Ungraded',
  },

  orgSettings: {
    title: 'Organization settings',
    subtitle:
      'Per-workflow toggles for your organization. Changes log to audit_logs as tenant.feature_flags_changed.',
    on: 'On',
    off: 'Off',
    failedToSave: 'Failed to save the setting.',
    knownFlagCount:
      'Known flags: {{count}}. To add another contract-driven workflow toggle, extend KNOWN_FLAGS in useTenantFeatures and the FLAGS array on this page.',
  },

  nav: {
    primary: 'Primary navigation',
    openMenu: 'Open menu',
    dashboard: 'Dashboard',
    rooms: 'Rooms',
    newTask: '+ New task',
    bulkNewTask: 'Bulk create',
    team: 'Team',
    anomalies: 'Anomalies',
    feeRequests: 'Fee requests',
    abandonedBelongings: 'Abandoned belongings',
    reports: 'Reports',
    bidTool: 'Bid tool',
    leads: 'Leads',
    corrections: 'MCP corrections',
    orgSettings: 'Settings',
    viewingCampus: 'Viewing:',
  },

  common: {
    yes: 'Yes',
    no: 'No',
    cancel: 'Cancel',
  },

  aiStatus: {
    bannerTitle:  'ReadyScan verification is currently unavailable.',
    bannerDetail: 'Supervisors will need to manually verify photo coverage before rooms can be marked ready.',
  },

  layout: {
    offlineQueued: 'Offline — {{count}} action{{plural}} queued.',
    offlineNoQueue:
      'Offline — your changes will be sent when you reconnect.',
    reconnecting: 'Reconnecting — {{count}} action{{plural}} pending.',
  },

  error: {
    somethingWentWrong: 'Something went wrong',
    sectionFailed: 'This section failed to load',
    globalDetail:
      'An unexpected error occurred. Reload the page to try again.',
    routeDetail:
      'An unexpected error occurred in this section. You can retry or reload the page.',
    reloadPage: 'Reload page',
    retry: 'Retry',
    unexpectedError: 'An unexpected error occurred',
  },

  settings: {
    language: 'Language',
    english: 'EN',
    spanish: 'ES',
  },

  serverErrors: {
    incompleteChecklist: 'The checklist is not complete. Please finish all required items.',
    invalidTransition: 'This room cannot move to that status right now.',
    missingPhotos: 'Required photos are missing. Please upload them before submitting.',
    rateLimited: 'Too many requests. Please wait a moment and try again.',
    unauthorized: 'You are not signed in. Please sign in and try again.',
    forbidden: 'You do not have permission to perform this action.',
    notFound: 'The requested item was not found.',
    taskAlreadyCompleted: 'This task has already been completed.',
    roomNotReady: 'The room is not ready for this transition.',
    blockingIssues: 'There are blocking maintenance issues that must be resolved first.',
    operationFailed: 'The operation failed. Please try again.',
  },
} as const

export default en
